import {
  useRef,
  useEffect,
  type ReactNode,
  useState,
  useCallback,
} from "react";
import { cn } from "@/lib/utils";
import gsap from "gsap";
import { Badge } from "@/components/ui/badge";

interface CarouselProps {
  children: ReactNode[];
  className?: string;
  autoPlay?: boolean;
  autoPlayInterval?: number;
  gap?: number;
}

export const Carousel = ({
  children,
  className,
  autoPlay = false,
  autoPlayInterval = 4000,
  gap = 24,
}: CarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [itemWidth, setItemWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const currentX = useRef(0);
  const velocity = useRef(0);
  const lastX = useRef(0);
  const lastTime = useRef(0);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const itemCount = children.length;

  // Calculate boundaries
  const getTrackWidth = useCallback(() => {
    return itemCount * itemWidth - gap;
  }, [itemCount, itemWidth, gap]);

  const getMinX = useCallback(() => {
    const trackWidth = getTrackWidth();
    return -(trackWidth - containerWidth);
  }, [getTrackWidth, containerWidth]);

  useEffect(() => {
    if (!trackRef.current || !containerRef.current) return;

    const updateDimensions = () => {
      const firstItem = trackRef.current?.children[0] as HTMLElement;
      if (firstItem) {
        setItemWidth(firstItem.offsetWidth + gap);
      }
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [gap, children]);

  // Autoplay
  useEffect(() => {
    if (!autoPlay || !trackRef.current || !itemWidth) return;

    const animate = () => {
      const current = gsap.getProperty(trackRef.current, "x") as number;
      const minX = getMinX();

      // Slowly scroll left
      const newX = current - 1;

      // When reaching the end, spring back to start
      if (newX < minX) {
        gsap.to(trackRef.current, {
          x: 0,
          duration: 2,
          ease: "elastic.out(3, 0.5)",
        });
        return;
      }

      gsap.set(trackRef.current, { x: newX });
    };

    autoPlayRef.current = setInterval(animate, autoPlayInterval / 100);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [autoPlay, autoPlayInterval, itemWidth, getMinX]);

  const applyBoundarySpring = useCallback(
    (targetX: number) => {
      if (!trackRef.current) return;

      const minX = getMinX();
      const maxX = 0;

      let finalX = targetX;
      let ease = "power3.out";
      let duration = 0.6;

      // Apply spring effect at boundaries
      if (targetX > maxX) {
        finalX = maxX;
        ease = "elastic.out(1, 0.4)";
        duration = 0.8;
      } else if (targetX < minX) {
        finalX = minX;
        ease = "elastic.out(1, 0.4)";
        duration = 0.8;
      }

      gsap.to(trackRef.current, {
        x: finalX,
        duration,
        ease,
      });
    },
    [getMinX],
  );

  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    isDragging.current = true;
    startX.current = "touches" in e ? e.touches[0].clientX : e.clientX;
    currentX.current = gsap.getProperty(trackRef.current, "x") as number;
    lastX.current = startX.current;
    lastTime.current = Date.now();
    velocity.current = 0;

    // Kill any ongoing animations
    gsap.killTweensOf(trackRef.current);

    // Stop autoplay while dragging
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging.current || !trackRef.current) return;

    const x = "touches" in e ? e.touches[0].clientX : e.clientX;
    const diff = x - startX.current;
    const now = Date.now();
    const dt = now - lastTime.current;

    // Calculate velocity
    if (dt > 0) {
      velocity.current = (x - lastX.current) / dt;
    }

    lastX.current = x;
    lastTime.current = now;

    let newX = currentX.current + diff;
    const minX = getMinX();
    const maxX = 0;

    // Apply resistance at boundaries (rubberband effect)
    if (newX > maxX) {
      const overscroll = newX - maxX;
      newX = maxX + overscroll * 0.3;
    } else if (newX < minX) {
      const overscroll = minX - newX;
      newX = minX - overscroll * 0.3;
    }

    gsap.set(trackRef.current, { x: newX });
  };

  const handleDragEnd = () => {
    if (!isDragging.current || !trackRef.current) return;
    isDragging.current = false;

    const currentPos = gsap.getProperty(trackRef.current, "x") as number;

    // Apply momentum based on velocity
    const momentum = velocity.current * 200;
    const targetX = currentPos + momentum;

    applyBoundarySpring(targetX);
  };

  return (
    <div className={cn("relative w-full", className)}>
      {/* Wrapper with hidden overflow to prevent scrollbars */}
      <div className="overflow-hidden">
        <div
          ref={containerRef}
          className="cursor-grab pb-8 active:cursor-grabbing"
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          <div ref={trackRef} className="flex" style={{ gap: `${gap}px` }}>
            {children.map((child, index) => (
              <div key={index} className="flex-shrink-0">
                {child}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Seamless fade overlay on the left edge */}
      {/* <div
        className="pointer-events-none absolute top-0 bottom-0 left-0 w-64"
        style={{
          background:
            "linear-gradient(to right, rgb(250, 249, 247) 0%, rgba(250, 249, 247, 0.95) 20%, rgba(250, 249, 247, 0.7) 50%, rgba(250, 249, 247, 0) 100%)",
        }}
      /> */}
      {/* Seamless fade overlay on the right edge */}
      {/* <div
        className="pointer-events-none absolute top-0 right-0 bottom-0 w-64"
        style={{
          background:
            "linear-gradient(to left, rgb(250, 249, 247) 0%, rgba(250, 249, 247, 0.95) 20%, rgba(250, 249, 247, 0.7) 50%, rgba(250, 249, 247, 0) 100%)",
        }}
      /> */}
    </div>
  );
};

interface CarouselCardProps {
  icon?: ReactNode;
  title: string;
  description: string;
  badge?: ReactNode;
  className?: string;
}

export const CarouselCard = ({
  icon,
  title,
  description,
  badge,
  className,
}: CarouselCardProps) => {
  return (
    <div
      className={cn(
        "flex h-[500px] w-[440px] flex-col justify-between rounded-[20px] bg-gradient-to-b from-white to-[#FFA96D]/10 p-6 shadow-md transition-all duration-300 select-none",
        className,
      )}
    >
      <div className="flex h-full flex-col justify-between">
        {/* Badge at top */}
        {badge && (
          <Badge variant="source" className="border-neutral-400">
            {badge}
          </Badge>
        )}

        {icon && (
          <div className="flex items-center justify-center pb-6">{icon}</div>
        )}
        <div className="flex h-[150px] flex-col space-y-0.5">
          <h3 className="text-brand-500 text-2xl leading-9 font-bold -tracking-wide">
            {title}
          </h3>
          <p className="text-[18px] leading-7 font-medium text-neutral-700">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};
